import { Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';

/** Longitud mínima para contraseña de token virtual (activación y restablecimiento). */
export const PASSWORD_MIN_LENGTH = 8;
/** Longitud máxima para contraseña de token virtual clásico. */
export const PASSWORD_MAX_LENGTH = 12;

/** CCSW5 / código de verificación: longitud fija 10–12. */
export const PASSWORD_CCSW5_MIN_LENGTH = 10;
export const PASSWORD_CCSW5_MAX_LENGTH = 12;

/** Al menos una mayúscula (incluye Á, É, Í, Ó, Ú, Ñ). */
const REGEX_UPPER = /[A-ZÁÉÍÓÚÑ]/;
/** Al menos un dígito. */
const REGEX_NUMBER = /\d/;
/** Al menos un carácter que no sea letra ni número (símbolo). */
const REGEX_SPECIAL = /[^A-Za-z0-9]/;

/**
 * Patrón que exige: 8–12 caracteres, al menos una mayúscula, un número y un carácter especial.
 * Misma política que restablecer contraseña token virtual.
 */
export const PASSWORD_PATTERN = /^(?=.*[A-ZÁÉÍÓÚÑ])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,12}$/;

/**
 * Patrón CCSW5: 10–12 caracteres, mayúscula, número y carácter especial.
 */
export const PASSWORD_CCSW5_PATTERN = /^(?=.*[A-ZÁÉÍÓÚÑ])(?=.*\d)(?=.*[^A-Za-z0-9]).{10,12}$/;

/**
 * Validadores para contraseña de token virtual (activación y cambio de contraseña).
 * Coherencia entre ambos flujos.
 */
export function tokenVirtualPasswordValidators(): ValidatorFn[] {
  return [
    Validators.required,
    Validators.minLength(PASSWORD_MIN_LENGTH),
    Validators.maxLength(PASSWORD_MAX_LENGTH),
    Validators.pattern(PASSWORD_PATTERN)
  ];
}

/** Validadores CCSW5: 10–12 caracteres + mayúscula, número y especial. */
export function ccsw5PasswordValidators(): ValidatorFn[] {
  return [
    Validators.required,
    Validators.minLength(PASSWORD_CCSW5_MIN_LENGTH),
    Validators.maxLength(PASSWORD_CCSW5_MAX_LENGTH),
    Validators.pattern(PASSWORD_CCSW5_PATTERN)
  ];
}

/**
 * Validador que devuelve error con clave 'passwordPolicy' si no cumple mayúscula, número o especial.
 * Útil para mensajes específicos por regla (opcional).
 */
export function tokenVirtualPasswordPolicyValidator(control: AbstractControl): ValidationErrors | null {
  const v = control.value;
  if (v == null || v === '') return null;
  const s = String(v);
  if (s.length < PASSWORD_MIN_LENGTH) return null; // minLength ya lo cubre
  if (!REGEX_UPPER.test(s)) return { passwordPolicy: 'upper' };
  if (!REGEX_NUMBER.test(s)) return { passwordPolicy: 'number' };
  if (!REGEX_SPECIAL.test(s)) return { passwordPolicy: 'special' };
  return null;
}

/** Comprueba si la contraseña cumple longitud mínima. */
export function passwordHasMinLength(password: string): boolean {
  return (password ?? '').length >= PASSWORD_MIN_LENGTH;
}

/** Comprueba longitud 10–12 (política CCSW5). */
export function passwordHasCcsw5Length(password: string): boolean {
  const len = (password ?? '').length;
  return len >= PASSWORD_CCSW5_MIN_LENGTH && len <= PASSWORD_CCSW5_MAX_LENGTH;
}

/** Comprueba si tiene al menos una mayúscula. */
export function passwordHasUpper(password: string): boolean {
  return REGEX_UPPER.test(password ?? '');
}

/** Comprueba si tiene al menos un número. */
export function passwordHasNumber(password: string): boolean {
  return REGEX_NUMBER.test(password ?? '');
}

/** Comprueba si tiene al menos un carácter especial. */
export function passwordHasSpecial(password: string): boolean {
  return REGEX_SPECIAL.test(password ?? '');
}
